import React, {useContext, useEffect, useState} from 'react';
import {withRouter} from 'react-router-dom';
import {Section} from 'components/forms';
import {useClearForm, useFormPersistence} from 'components/forms/hooks';
import ReviewComponent from './ReviewComponent';
import SSLReview from './SSLReview';
import CircularProgress from '@material-ui/core/CircularProgress';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import FormNavigation, {
    defaultNext,
} from 'components/forms/context/FormNavigation';
import {
    usePostAssociate,
    usePostForm,
    usePostError,
    errorDataHelper,
} from 'hooks';
import {FormState} from 'components/forms/context';
import LoadingPage from 'components/LoadingPage';
import {usePutForm} from 'hooks/usePutEndpoint';
import sanitizeData from './sanitizer.js';
import {AppCtx} from 'context/AppContext';

// Take an array of errors and return an object with field name and error
const getFieldErrorMappings = errors => {
    return (
        errors?.reduce((mappings, errorObject) => {
            try {
                const fields = errorObject.fields;
                if (typeof fields === 'string') {
                    mappings[fields] = errorObject.message;
                } else if (Array.isArray(fields)) {
                    fields.forEach(fieldName => {
                        mappings[fieldName] = errorObject.message;
                    });
                }
            } catch (parseError) {
                console.error('Unable to parse fields for error', parseError);
            }
            return mappings;
        }, {}) ?? null
    );
};

const ReviewSection = ({
    history,
    fieldLabelMappings,
    showFormInfo,
    ssl = false,
    aops = false,
    endpointOverride = false,
    sanitize = true,
    isEnd = true,
    allowEdit = false,
    fields,
}) => {
    const {node, response, setResponse} = useContext(FormState);
    const {setNextButton} = useContext(FormNavigation);
    const {setTankermanId} = useContext(AppCtx);
    const {state} = useFormPersistence();
    const clearFormState = useClearForm(node);
    const fieldErrorMappings = getFieldErrorMappings(response?.errors);
    const generalErrors = response?.errors?.filter(
        e => !e.fields || !e.fields.length
    );

    // Support posting
    const postForm = usePostForm(endpointOverride || node);
    const putForm = usePutForm(`/api/1/user/${endpointOverride || node}`);
    const addForm = state.id ? putForm : postForm;
    const {post: postError} = usePostError();

    // Support associations
    const {
        post: postAssociate,
        error: associationError,
        submissionInProgress: associationInProgress,
    } = usePostAssociate();

    // UI Support
    const isPosting = addForm?.submissionInProgress || associationInProgress;
    const [hasAssociation, setHasAssociation] = useState(false);
    const [isWaiting, setIsWaiting] = useState(true);

    const submit = () => {
        const data = sanitize ? sanitizeData(state) : state;
        let formAction;
        if (state?.id) {
            formAction = addForm.put;
        } else {
            formAction = addForm.post;
        }
        formAction(data).then(response => {
            //                  id from post             || id from form select
            const sslId = response?.form_input?.id || state?._ssl_id;
            const tankermanId =
                response?.tankerman?.id || state?.user_tankerman_id;
            if (sslId && tankermanId) {
                setHasAssociation(true);
                postAssociate({
                    maindoc_type: 'ssl',
                    maindoc_id: sslId,
                    otherdoc_type: 'user_tankerman',
                    otherdoc_id: tankermanId,
                }).then(() => {
                    setIsWaiting(false);
                });
            } else {
                setIsWaiting(false);
            }
        });
    };

    let validateButton = {
        ...defaultNext,
    };
    if (isEnd) {
        validateButton = {
            ...defaultNext,
            testHook: 'form-nav-validate',
            disabled: isPosting,
            label: isPosting ? '' : 'Validate',
            icon: isPosting ? CircularProgress : KeyboardArrowRight,
            onClick: submit,
        };
    }

    // Set default to next trigger validation
    useEffect(() => {
        setNextButton(validateButton);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isWaiting) {
            setResponse(addForm?.result || addForm?.error);
            if (
                addForm?.result?.errors?.length ||
                addForm?.error?.errors?.length ||
                (hasAssociation && associationError)
            ) {
                setNextButton({
                    ...defaultNext,
                    testHook: 'form-nav-abort',
                    label: 'Abort',
                    color: 'secondary',
                    icon: KeyboardArrowRight,
                    onClick: () => {
                        clearFormState();
                        history.push('/');
                        return false;
                    },
                });

                // Scroll to top since we have errors
                window.scrollTo(0, 0);

                // post feedback
                postError(
                    errorDataHelper({
                        feedback_text: `[AUTO GENERATED][FORM REVIEW][FORM ERROR] Error Messages shown to user: (result): ${JSON.stringify(
                            addForm?.result?.errors,
                            null,
                            2
                        )} (errors): ${JSON.stringify(
                            addForm?.error?.errors,
                            null,
                            2
                        )} (assocation errors): ${JSON.stringify(
                            associationError,
                            null,
                            2
                        )}`,
                    })
                );
            } else if (state?._ssl_attached === 'true' && !state?._ssl_id) {
                // associated ssl but no ssl provided. lets forward them to ssl
                setTankermanId(addForm?.result?.tankerman?.id || null);
                clearFormState();
                history.push('/forms/ssl/start');
            } else {
                history.push(`/forms/${node}/success`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isWaiting, addForm?.result, addForm?.error, setResponse]);

    const Component = ssl ? SSLReview : ReviewComponent;

    if (isPosting) {
        return <LoadingPage />;
    } else {
        return (
            <Section>
                <Component
                    state={state || {}}
                    showFormInfo={showFormInfo}
                    fieldLabelMappings={fieldLabelMappings}
                    fieldErrorMappings={fieldErrorMappings}
                    generalErrors={generalErrors}
                    isEnd={isEnd}
                    allowEdit={allowEdit}
                    aops={aops}
                    fields={fields}
                    jsonMap={fields?._jsonMap}
                />
            </Section>
        );
    }
};

export default withRouter(ReviewSection);
